import { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import MentoringAgreementTile from './MentoringAgreementTile';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePairing } from '../context/PairingContext';

export default function FeedbackTabs({ userId, user }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [allMentors, setAllMentors] = useState([]);
  const [mentorId, setMentorId] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [openFeedback, setOpenFeedback] = useState(null);
  const { t } = useTranslation();
  const { selectedMatchingId } = usePairing();

  console.log('[FeedbackTabs] initial userId:', userId);

  // Feedback-Status ist matching-skopiert → bei Pairing-Wechsel neu laden
  useEffect(() => {
    if (!userId) return;

    async function fetchFeedbackStatus() {
      try {
        const response = await axios.get(
          `/mentoring/feedback/status/${userId}${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`
        );
        if (response.data?.open) {
          setOpenFeedback({
            milestone: response.data.milestone || 'kennenlerntermin',
          });
        } else {
          setOpenFeedback(null);
        }
      } catch {
        setOpenFeedback(null);
      }
    }

    fetchFeedbackStatus();
  }, [userId, selectedMatchingId]);

  // Alle Matched-Mentoren (Plural) + Historie: einmal pro Mentee laden,
  // kein Re-Fetch bei Pairing-Wechsel (Muster wie MenteeDashboard)
  useEffect(() => {
    if (!userId) {
      setAllMentors([]);
      return;
    }

    async function fetchMatchedMentors() {
      try {
        // ✅ Plural-Route: Array, jedes Element hat matching_id und mentor_id
        const res = await axios.get(
          `/mentoring/mentee/${userId}/matched-mentors`
        );
        setAllMentors(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn(
          '[FeedbackTabs] error loading matched-mentors',
          err
        );
        setAllMentors([]);
      }
    }

    async function fetchFeedbackHistory() {
      try {
        const res = await axios.get(
          `/mentoring/feedback_results/mentee/${userId}`,
          { withCredentials: true }
        );
        setFeedbacks(res.data);
      } catch {}
    }

    fetchMatchedMentors();
    fetchFeedbackHistory();
  }, [userId]);

  // Aktiven Mentor anhand selectedMatchingId auswählen (nur Auswahl, kein Re-Fetch).
  // Fallback: erstes Element. Leeres Array → mentorId = null.
  useEffect(() => {
    if (allMentors.length === 0) {
      setMentorId(null);
      return;
    }
    const picked = selectedMatchingId
      ? allMentors.find((m) => m.matching_id === selectedMatchingId)
      : null;
    const chosen = picked || allMentors[0];
    if (chosen && chosen.mentor_id) {
      setMentorId(chosen.mentor_id);
    } else {
      console.warn(
        '[FeedbackTabs] matched-mentors: keine mentor_id im gewählten Pairing',
        chosen
      );
      setMentorId(null);
    }
  }, [allMentors, selectedMatchingId]);

  // Debug-Logging, um den Zustand zu sehen
  useEffect(() => {
    console.log('[FeedbackTabs] state:', { openFeedback, mentorId, feedbacks });
  }, [openFeedback, mentorId, feedbacks]);

  function safeParse(json) {
    try {
      const parsed = JSON.parse(json);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  const handleSelectFeedback = (entry) => {
    const parsed = {
      ...entry,
      goals:
        typeof entry.goals === 'string'
          ? safeParse(entry.goals)
          : entry.goals || {},
      competences:
        typeof entry.competences === 'string'
          ? safeParse(entry.competences)
          : entry.competences || {},
    };
    setSelectedFeedback(parsed);
  };

  return (
    <div className="text-white mt-2">
      {/* Feedback Card */}
      <div className="mt-6 flex flex-col gap-6">
        <div className="rounded-2xl bg-[#1b222c] shadow-2xl border border-[#262F41] overflow-hidden">
          <div className="bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] px-6 py-3">
            <h3 className="font-bold text-xl text-black tracking-tight">
              {t('feedbackTabs.title')}
            </h3>
          </div>
          <div className="p-6">
            {/* Offenes Feedback */}
            {openFeedback?.milestone && mentorId ? (
              <div className="mb-5 p-4 rounded-xl bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <span>
                    {t('feedbackTabs.openFeedback')}{' '}
                    <strong className="text-yellow-700">
                      {t(`milestone.${openFeedback.milestone}`)}
                    </strong>
                    .
                  </span>
                </div>
                <a
                  href={`#/feedback?uid=${userId}&mentor_id=${mentorId}&milestone=${openFeedback.milestone}&role=mentee${selectedMatchingId ? `&matching_id=${selectedMatchingId}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 md:mt-0 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black px-4 py-2 rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition"
                >
                  {t('feedbackTabs.fillNow')}
                </a>
              </div>
            ) : (
              <div className="mb-5 text-gray-400 px-3 py-2 rounded bg-[#252d38] inline-block">
                {t('feedbackTabs.noOpenFeedback')}
              </div>
            )}

            {/* Feedback Historie */}
            <h4 className="font-semibold mb-2 text-white">
              {t('feedbackTabs.pastFeedbacks')}
            </h4>
            {feedbacks.length === 0 ? (
              <div className="bg-[#131a22] text-gray-400 p-6 rounded-2xl text-center">
                {t('feedbackTabs.noFeedbacks')}
              </div>
            ) : (
              <ul className="space-y-2">
                {feedbacks.map((entry) => (
                  <li key={entry.id}>
                    <button
                      onClick={() => handleSelectFeedback(entry)}
                      className="text-[#8FFF8B] underline hover:text-[#53DD6C] font-semibold transition"
                    >
                      {t(`milestone.${entry.milestone}`)} –{' '}
                      {entry.submitted_at
                        ? new Date(entry.submitted_at).toLocaleDateString()
                        : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Detail Popup */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-xl w-full max-h-[90vh] overflow-y-auto text-black">
            <div className="flex items-center mb-3">
              <CheckCircle2 className="text-[#53DD6C] w-8 h-8 mr-2" />
              <h3 className="text-2xl font-bold">
                {t('feedbackTabs.detailTitle', {
                  milestone: t(`milestone.${selectedFeedback.milestone}`),
                })}
              </h3>
            </div>
            <div className="text-base text-gray-800 space-y-2">
              <div>
                <strong>{t('feedbackTabs.date')}:</strong>{' '}
                {selectedFeedback.submitted_at
                  ? new Date(selectedFeedback.submitted_at).toLocaleString()
                  : ''}
              </div>
              <div>
                <strong>{t('feedbackTabs.satisfaction')}:</strong>{' '}
                {selectedFeedback.satisfaction_score
                  ? selectedFeedback.satisfaction_score + '/5'
                  : '–'}
              </div>
              <div>
                <strong>{t('feedbackTabs.mentorRating')}:</strong>{' '}
                {selectedFeedback.mentor_rating != null
                  ? selectedFeedback.mentor_rating + '/5'
                  : '–'}
              </div>

              {selectedFeedback.comments && (
                <div>
                  <strong>{t('feedbackTabs.comment')}:</strong>
                  <div className="bg-gray-100 p-3 rounded mt-1">
                    {selectedFeedback.comments}
                  </div>
                </div>
              )}
              {selectedFeedback.goals &&
                Object.keys(selectedFeedback.goals).length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-1">
                      {t('feedbackTabs.goalRatings')}
                    </h4>
                    <ul className="list-disc ml-6">
                      {Object.entries(selectedFeedback.goals).map(
                        ([key, val]) => (
                          <li key={key}>
                            <span className="font-medium">{key}</span>: {val}/5
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}
              {selectedFeedback.competences &&
                Object.keys(selectedFeedback.competences).length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-1">
                      {t('feedbackTabs.competenceRatings')}
                    </h4>
                    <ul className="list-disc ml-6">
                      {Object.entries(selectedFeedback.competences).map(
                        ([key, val]) => (
                          <li key={key}>
                            <span className="font-medium">{key}</span>: {val}/5
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}
            </div>
            <button
              className="mt-6 px-6 py-2 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold hover:from-[#53DD6C] hover:to-[#8FFF8B] transition"
              onClick={() => setSelectedFeedback(null)}
              autoFocus
            >
              {t('feedbackTabs.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


